import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import moment from "moment";
import {
  formatPreFinanceMoney,
  groupVatSourcesBySupplier,
} from "../utils/preFinancePreviewUtils";

const mapInvoiceToSource = (invoice) => ({
  sourceId: invoice.customerInvoiceId ?? invoice.CustomerInvoiceId,
  sourceCode: invoice.invoiceNumber ?? invoice.InvoiceNumber,
  sourceDate: invoice.invoiceDate ?? invoice.InvoiceDate,
  supplierId: invoice.supplierId ?? invoice.SupplierId,
  supplierName: invoice.supplierName ?? invoice.SupplierName,
  grossAmount: invoice.grossAmount ?? invoice.GrossAmount,
  refundAmount: invoice.vatAmountLocal ?? invoice.VatAmountLocal,
  declarationAmount: invoice.vatAmountLocal ?? invoice.VatAmountLocal,
  vatAmountEur: invoice.vatAmountEur ?? invoice.VatAmountEur,
});

const SummaryCard = ({ title, summary }) => (
  <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
    <Typography variant="subtitle2" gutterBottom>
      {title}
    </Typography>
    <Typography variant="body2">{summary.count ?? 0} invoice(s)</Typography>
    <Typography variant="body2">
      VAT local {formatPreFinanceMoney(summary.vatAmountLocal)}
    </Typography>
    <Typography variant="body2">VAT EUR {formatPreFinanceMoney(summary.vatAmountEur)}</Typography>
  </Paper>
);

const SupplierInvoiceGroups = ({ invoices, ariaLabelPrefix, showVfColumn = false }) => {
  const supplierGroups = useMemo(
    () => groupVatSourcesBySupplier((invoices ?? []).map(mapInvoiceToSource)),
    [invoices]
  );

  if (supplierGroups.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        None
      </Typography>
    );
  }

  return supplierGroups.map((supplier) => (
    <Accordion
      key={`${ariaLabelPrefix}-${supplier.supplierId ?? supplier.supplierName}`}
      defaultExpanded={false}
      disableGutters
      sx={{ mb: 2 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box>
          <Typography variant="subtitle2">{supplier.supplierName}</Typography>
          <Typography variant="caption" color="text.secondary">
            {supplier.sources.length} invoice(s) · VAT local {formatPreFinanceMoney(supplier.totalRefundAmount)}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" stickyHeader aria-label={`${ariaLabelPrefix} for ${supplier.supplierName}`}>
            <TableHead>
              <TableRow>
                <TableCell>Invoice</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                {showVfColumn && <TableCell>VF</TableCell>}
                <TableCell align="right">Net</TableCell>
                <TableCell align="right">VAT local</TableCell>
                <TableCell align="right">VAT EUR</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {supplier.sources.map((source) => {
                const invoice = invoices.find(
                  (item) => (item.customerInvoiceId ?? item.CustomerInvoiceId) === source.sourceId
                );
                const isVf = (invoice?.preFinanceId ?? invoice?.PreFinanceId ?? 0) > 0;

                return (
                  <TableRow key={source.sourceId}>
                    <TableCell>{source.sourceCode}</TableCell>
                    <TableCell>
                      {source.sourceDate ? moment(source.sourceDate).format("DD-MM-YYYY") : "—"}
                    </TableCell>
                    <TableCell>
                      {invoice?.invoiceStateShortCode ?? invoice?.InvoiceStateShortCode ?? "—"}
                    </TableCell>
                    {showVfColumn && (
                      <TableCell>
                        {isVf ? <Chip size="small" label="VF" color="primary" variant="outlined" /> : "—"}
                      </TableCell>
                    )}
                    <TableCell align="right">{formatPreFinanceMoney(source.grossAmount)}</TableCell>
                    <TableCell align="right">{formatPreFinanceMoney(source.refundAmount)}</TableCell>
                    <TableCell align="right">{formatPreFinanceMoney(source.vatAmountEur)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  ));
};

const ClaimLockTncConfirmDialog = ({ isOpen, preview, onConfirm, onCancel, isBusy }) => {
  const [acceptVfToRfc, setAcceptVfToRfc] = useState(false);
  const [keepNonVfInvoicesAsRfc, setKeepNonVfInvoicesAsRfc] = useState(false);
  const includedSummary = preview?.includedSummary ?? preview?.IncludedSummary ?? {};
  const vfSummary = preview?.vfSummary ?? preview?.VfSummary ?? {};
  const vfNeedsRfc = preview?.vfNeedsRfc ?? preview?.VfNeedsRfc ?? [];
  const nonVfToTnc = preview?.nonVfToTnc ?? preview?.NonVfToTnc ?? [];
  const excluded = preview?.excluded ?? preview?.Excluded ?? [];
  const hasVfInvoices = preview?.hasVfInvoices ?? preview?.HasVfInvoices ?? false;
  const willApplyAutomaticTnc =
    preview?.wouldApplyAutomaticTnc ?? preview?.WouldApplyAutomaticTnc ?? false;
  const hasVfNeedsRfc = vfNeedsRfc.length > 0;
  const vfNeedsRfcCount = vfNeedsRfc.length;
  const vfNeedsRfcSingular = vfNeedsRfcCount === 1;
  const nonVfToTncCount = nonVfToTnc.length;
  const nonVfToTncSingular = nonVfToTncCount === 1;
  const canConfirm = !hasVfNeedsRfc || acceptVfToRfc;

  useEffect(() => {
    if (isOpen) {
      setAcceptVfToRfc(false);
      setKeepNonVfInvoicesAsRfc(false);
    }
  }, [isOpen, preview]);

  const handleConfirm = () => {
    onConfirm({
      confirmed: true,
      convertVfToRfc: hasVfNeedsRfc && acceptVfToRfc,
      keepNonVfInvoicesAsRfc: hasVfInvoices && nonVfToTncCount > 0 && keepNonVfInvoicesAsRfc,
    });
  };

  const handleCancel = () => {
    onCancel({
      confirmed: false,
      convertVfToRfc: false,
      keepNonVfInvoicesAsRfc: false,
    });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      disableRestoreFocus
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      aria-labelledby="claim-lock-preview-dialog-title"
      aria-describedby="claim-lock-preview-dialog-description"
    >
      <DialogTitle id="claim-lock-preview-dialog-title">Confirm claim lock</DialogTitle>
      <DialogContent>
        <DialogContentText id="claim-lock-preview-dialog-description" sx={{ mb: 2 }}>
          Review what will be included in the locked claim
          {excluded.length > 0 || hasVfNeedsRfc ? " and resolve any VF status issues." : "."}
        </DialogContentText>

        {hasVfNeedsRfc && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {vfNeedsRfcSingular
              ? "1 VF invoice is not RFC. Locking is blocked unless you accept converting it to RFC below."
              : `${vfNeedsRfcCount} VF invoices are not RFC. Locking is blocked unless you accept converting them to RFC below.`}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <SummaryCard title="Included in claim" summary={includedSummary} />
          </Grid>
          <Grid item xs={12} md={6}>
            <SummaryCard title="Included VF invoices (already RFC)" summary={vfSummary} />
          </Grid>
        </Grid>

        {hasVfNeedsRfc && (
          <>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {vfNeedsRfcSingular
                ? "VF invoice that must become RFC"
                : "VF invoices that must become RFC"}
            </Typography>
            <SupplierInvoiceGroups
              invoices={vfNeedsRfc}
              ariaLabelPrefix={
                vfNeedsRfcSingular
                  ? "VF invoice that must become RFC"
                  : "VF invoices that must become RFC"
              }
              showVfColumn
            />
            <FormControlLabel
              sx={{
                mb: 2,
                ml: 0,
                mr: 0,
                alignItems: "center",
                gap: 1,
                "& .MuiCheckbox-root": {
                  padding: 0,
                },
                "& .MuiFormControlLabel-label": {
                  marginLeft: 0,
                },
              }}
              control={
                <Checkbox
                  checked={acceptVfToRfc}
                  onChange={(event) => setAcceptVfToRfc(event.target.checked)}
                  inputProps={{
                    "aria-label": vfNeedsRfcSingular
                      ? "Accept converting this VF invoice to RFC"
                      : "Accept converting these VF invoices to RFC",
                  }}
                />
              }
              label={
                <Typography variant="body2" component="span">
                  {vfNeedsRfcSingular
                    ? "I agree to set this VF invoice to RFC and continue locking the claim."
                    : `I agree to set these ${vfNeedsRfcCount} VF invoices to RFC and continue locking the claim.`}
                </Typography>
              }
            />
          </>
        )}

        {hasVfInvoices && nonVfToTncCount > 0 && (
          <>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {nonVfToTncSingular
                ? "Non-VF invoice that will move to TNC"
                : "Non-VF invoices that will move to TNC"}
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              {nonVfToTncSingular
                ? "This non-prefinanced invoice will be moved to TNC when you confirm, unless you choose to keep it as RFC below."
                : "These non-prefinanced invoices will be moved to TNC when you confirm, unless you choose to keep them as RFC below."}
            </Alert>
            <SupplierInvoiceGroups
              invoices={nonVfToTnc}
              ariaLabelPrefix={
                nonVfToTncSingular
                  ? "Non-VF invoice that will move to TNC"
                  : "Non-VF invoices that will move to TNC"
              }
            />
            <FormControlLabel
              sx={{
                mb: 2,
                ml: 0,
                mr: 0,
                alignItems: "center",
                gap: 1,
                "& .MuiCheckbox-root": {
                  padding: 0,
                },
                "& .MuiFormControlLabel-label": {
                  marginLeft: 0,
                },
              }}
              control={
                <Checkbox
                  checked={keepNonVfInvoicesAsRfc}
                  onChange={(event) => setKeepNonVfInvoicesAsRfc(event.target.checked)}
                  inputProps={{
                    "aria-label": nonVfToTncSingular
                      ? "Keep this non-VF invoice as RFC in the claim"
                      : "Keep these non-VF invoices as RFC in the claim",
                  }}
                />
              }
              label={
                <Typography variant="body2" component="span">
                  {nonVfToTncSingular
                    ? "Keep this non-VF invoice as RFC in this claim."
                    : `Keep these ${nonVfToTncCount} non-VF invoices as RFC in this claim.`}
                </Typography>
              }
            />
          </>
        )}

        {excluded.length > 0 && (
          <>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Not included in claim
            </Typography>
            {willApplyAutomaticTnc && !hasVfInvoices ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                These non-prefinanced invoices will be moved to TNC when you confirm.
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                These invoices will stay outside the claim. Their status will not change automatically.
              </Alert>
            )}
            <SupplierInvoiceGroups invoices={excluded} ariaLabelPrefix="Excluded invoices" />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary" disabled={isBusy} aria-label="Cancel claim lock">
          No
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          autoFocus
          disabled={isBusy || !canConfirm}
          aria-label="Confirm claim lock"
        >
          Yes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClaimLockTncConfirmDialog;
